import { Capacitor } from '@capacitor/core';
import { LocalNotifications } from '@capacitor/local-notifications';
import type { Flight, Overlap } from '../types';
import { getCityName } from '../utils/airports';
import { toDateStr } from '../utils/dates';

const STORAGE_KEY = 'overlap-notif-hash';
const STORAGE_IDS_KEY = 'overlap-notif-ids';
const NOTIF_ID_START = 2000;
const NOTIF_ID_END = 2999;
const MAX_NOTIFICATIONS = 5;

function computeHash(flights: Flight[], overlaps: Overlap[]): string {
  const flightKeys = flights.map((f) => `${f.id}:${f.date}`).sort().join(',');
  const overlapKeys = overlaps.map((o) => `${o.id}:${o.city}:${o.startDate}`).sort().join(',');
  return `${flightKeys}|${overlapKeys}`;
}

function getStoredIds(): number[] {
  try {
    const raw = localStorage.getItem(STORAGE_IDS_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function storeIds(ids: number[]): void {
  localStorage.setItem(STORAGE_IDS_KEY, JSON.stringify(ids));
}

export async function scheduleOverlapNotifications(
  flights: Flight[],
  overlaps: Overlap[],
  baseAirport: string,
  rosterActive: boolean,
  mutedFriendIds: Set<string>,
  isPremium: boolean = false,
): Promise<void> {
  if (!Capacitor.isNativePlatform()) return;

  // Free users don't get overlap push notifications
  if (!isPremium) {
    // Cancel any previously scheduled notifications
    const prevIds = getStoredIds();
    if (prevIds.length > 0) {
      try {
        await LocalNotifications.cancel({
          notifications: prevIds.map((id) => ({ id })),
        });
      } catch { /* ignore */ }
      storeIds([]);
    }
    return;
  }

  // Skip if data hasn't changed
  const hash = computeHash(flights, overlaps);
  const prevHash = localStorage.getItem(STORAGE_KEY);
  if (hash === prevHash) return;
  localStorage.setItem(STORAGE_KEY, hash);

  // Cancel previously scheduled overlap notifications
  const prevIds = getStoredIds();
  if (prevIds.length > 0) {
    try {
      await LocalNotifications.cancel({
        notifications: prevIds.map((id) => ({ id })),
      });
    } catch { /* ignore */ }
  }

  const today = toDateStr(new Date());
  const notifications: Array<{
    id: number;
    title: string;
    body: string;
    schedule: { at: Date };
    extra: { screen: string };
  }> = [];
  let nextId = NOTIF_ID_START;

  // Build a map of cities where friends are on each future date
  const friendCityDates = new Map<string, Set<string>>(); // city -> set of dates
  for (const overlap of overlaps) {
    // Filter muted friends
    const activeFriends = overlap.friends.filter(
      (f) => !mutedFriendIds.has(f.user.id),
    );
    if (activeFriends.length === 0) continue;

    const start = new Date(overlap.startDate + 'T00:00:00');
    const end = new Date(overlap.endDate + 'T00:00:00');
    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
      const dateStr = toDateStr(d);
      if (dateStr <= today) continue;
      const key = overlap.city;
      if (!friendCityDates.has(key)) friendCityDates.set(key, new Set());
      friendCityDates.get(key)!.add(dateStr);
    }
  }

  // For each future flight arriving at a non-base city where friends are
  const futureFlights = flights
    .filter((f) => f.date > today)
    .sort((a, b) => a.date.localeCompare(b.date));

  for (const flight of futureFlights) {
    if (notifications.length >= MAX_NOTIFICATIONS) break;
    if (nextId > NOTIF_ID_END) break;

    const arrCity = flight.arrivalAirport;
    if (arrCity === baseAirport) continue;

    const friendDates = friendCityDates.get(arrCity);
    if (!friendDates?.has(flight.date)) continue;

    const cityName = getCityName(arrCity);
    const arrivalDate = new Date(`${flight.date}T${flight.arrivalTime || '12:00'}:00`);

    const title = rosterActive
      ? `Friends in ${cityName}!`
      : `Crew in ${cityName}!`;
    const body = rosterActive
      ? `You're arriving in ${cityName} — check who else is there`
      : `Upload your roster to see if friends are in ${cityName}`;

    notifications.push({
      id: nextId++,
      title,
      body,
      schedule: { at: arrivalDate },
      extra: { screen: 'overlaps' },
    });
  }

  // For friends visiting base city — schedule morning notification on start date
  for (const overlap of overlaps) {
    if (notifications.length >= MAX_NOTIFICATIONS) break;
    if (nextId > NOTIF_ID_END) break;
    if (overlap.city !== baseAirport) continue;
    if (overlap.startDate <= today) continue;

    const activeFriends = overlap.friends.filter(
      (f) => !mutedFriendIds.has(f.user.id),
    );
    if (activeFriends.length === 0) continue;

    const cityName = getCityName(baseAirport);
    const morningDate = new Date(`${overlap.startDate}T09:00:00`);

    const title = rosterActive
      ? `Friends visiting ${cityName}!`
      : `Crew visiting ${cityName}!`;
    const body = rosterActive
      ? `${activeFriends.length} friend${activeFriends.length !== 1 ? 's' : ''} arriving at your base`
      : `Upload your roster to see who's visiting ${cityName}`;

    notifications.push({
      id: nextId++,
      title,
      body,
      schedule: { at: morningDate },
      extra: { screen: 'overlaps' },
    });
  }

  // Schedule all
  const newIds = notifications.map((n) => n.id);
  storeIds(newIds);

  if (notifications.length > 0) {
    try {
      await LocalNotifications.schedule({ notifications });
    } catch (err) {
      console.warn('[overlapNotifications] schedule failed:', err);
    }
  }
}

/** Listen for notification taps that should navigate to overlaps */
export function registerOverlapNotificationListener(
  onOpenOverlaps: () => void,
): void {
  if (!Capacitor.isNativePlatform()) return;

  LocalNotifications.addListener(
    'localNotificationActionPerformed',
    (action) => {
      if (action.notification.extra?.screen === 'overlaps') {
        onOpenOverlaps();
      }
    },
  );
}
